import { useState, useEffect, useCallback, useRef } from 'react';
import { useCalibration } from '../../context/calibrationContext';
import { useTestContext } from '../../context/testContext';
import { DistanceControlBlindspot } from '../../components';
import { usePostMessage, useReceiveMessage } from '../../context/messageContext';
import { calibrationConstants } from '../../helpers/calibration-constants';
import { SetDistanceView } from '../session/calibration/ClinicianCalibrationViews';
import { useCalibrationSync, toDistanceMode } from '../session/calibration/sync';

// C5 / C5.1 Set Distance. Logic from the former set-distance + DistanceControlPupillary +
// DistanceControlManual (same ws messages and maths):
//   faceRecognition -> 'pupillaryDistance'; Start/Stop Detection -> 'start|stopPupillaryDistanceDetection';
//                      the patient answers 'patientMovementDetected' {patientDistance, patientIsMoving}
//   manual          -> 'holding'; distanceFromScreen + blindSpotVanishingXDistancePx from the typed mm
//   blindspot       -> 'blindspot' (DistanceControlBlindspot, unchanged)
// The result is mirrored to the session state: distance {mode, mm, positioned}.
const SetDistance = ({ setTitle, setActiveTab }) => {
  const { updateTestData } = useTestContext();
  const {
    updateCalibrationData,
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected: storedMethod },
  } = useCalibration();
  const [methodSelected, setMethodSelected] = useState(storedMethod || 'faceRecognition');
  const [startedDetection, setStartedDetection] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const postMessage = usePostMessage();
  const messageFromPatient = useReceiveMessage();
  const { state, patchDistance } = useCalibrationSync();
  const { tangent15degrees } = calibrationConstants;
  const manualPatchTimer = useRef(null);

  useEffect(() => {
    setTitle('Set Distance');
  }, [setTitle]);

  useEffect(() => {
    if (state?.distance?.positioned && state?.distance?.mode === 'face') setPositioned(true);
  }, [state?.distance?.positioned, state?.distance?.mode]);

  useEffect(() => () => clearTimeout(manualPatchTimer.current), []);

  const selectMethod = (method) => {
    const usingMovementDetection = method === 'blindspot' || method === 'faceRecognition';
    updateCalibrationData({ methodSelected: method });
    updateTestData({ testMode: usingMovementDetection ? 'auto' : 'off' });
    setStartedDetection(false);
    setIsMoving(false);
    patchDistance({ mode: toDistanceMode(method), positioned: false });
    setPositioned(false);
    return setMethodSelected(method);
  };

  useEffect(() => {
    const postMessageType = {
      faceRecognition: 'pupillaryDistance',
      blindspot: 'blindspot',
    };
    postMessage({ type: postMessageType[methodSelected] || 'holding' });
  }, [methodSelected, postMessage]);

  // --- facial recognition ---------------------------------------------------------------------
  const toggleDetection = () => {
    postMessage({ type: startedDetection ? 'stopPupillaryDistanceDetection' : 'startPupillaryDistanceDetection' });
    setStartedDetection(!startedDetection);
    setIsMoving(false);
    if (!startedDetection) setPositioned(false);
  };

  const lastMovement = useRef(null);
  useEffect(() => {
    if (messageFromPatient?.type !== 'patientMovementDetected' || lastMovement.current === messageFromPatient) return;
    lastMovement.current = messageFromPatient;
    const moving = Boolean(messageFromPatient.content.patientIsMoving);
    const mm = parseInt(messageFromPatient.content.patientDistance);
    setIsMoving(moving);
    setStartedDetection(moving);
    const blindSpotVanishingXDistanceMm = mm * tangent15degrees;
    updateCalibrationData({
      blindSpotVanishingXDistancePx: blindSpotVanishingXDistanceMm / pixelDensity,
      distanceFromScreen: mm,
    });
    if (!moving && mm > 0) {
      setPositioned(true);
      patchDistance({ mode: 'face', mm, positioned: true });
    }
  }, [messageFromPatient, pixelDensity, tangent15degrees, updateCalibrationData, patchDistance]);

  // --- manual ---------------------------------------------------------------------------------
  const setManualDistance = useCallback(
    (manualDistanceMm) => {
      const mm = parseInt(manualDistanceMm);
      const blindSpotVanishingXDistanceMm = mm * tangent15degrees;
      updateCalibrationData({
        blindSpotVanishingXDistancePx: blindSpotVanishingXDistanceMm / pixelDensity,
        distanceFromScreen: mm,
      });
      clearTimeout(manualPatchTimer.current);
      manualPatchTimer.current = setTimeout(() => patchDistance({ mode: 'manual', mm, positioned: true }), 600);
    },
    [pixelDensity, tangent15degrees, updateCalibrationData, patchDistance],
  );

  const onManualChange = (value) => {
    setManualValue(value);
    if (value.length > 1 && parseInt(value)) setManualDistance(value);
  };

  return (
    <SetDistanceView
      method={methodSelected}
      onMethodChange={selectMethod}
      distanceMm={distanceFromScreen}
      detecting={startedDetection || isMoving}
      positioned={positioned}
      onToggleDetection={toggleDetection}
      manualValue={manualValue}
      onManualChange={onManualChange}
      blindspotControl={<DistanceControlBlindspot />}
      onBack={() => setActiveTab('calibration-selection')}
      onNext={() => setActiveTab('test')}
    />
  );
};

export default SetDistance;
